import React from "react";
import moment from "moment";
import {
  Layout,
  Card,
  Table,
  Row,
  Col,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Icon,
  message,
  Empty,
  Drawer,
  Typography,
  Descriptions,
  Timeline,
  Spin,
  Statistic,
  Divider,
  Alert
} from "antd";
import { apiGet } from "./utils/apiService";

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Alert type mapping
const ALERT_TYPE_MAP = {
  "FULL_TANK_MILEAGE_ALERT": "Full Tank to Full Tank Mileage",
  "IOT_BASED_MILEAGE_ALERT": "IoT Based Mileage",
  "DOCUMENT_RENEWAL_ALERT": "Document Renewal",
  "ASSET_AUDIT_DUE_ALERT": "Asset Audit Due",
  "MANPOWER_ENTRY_PENDING_ALERT": "Manpower Entry Pending"
};

// Severity mapping
const SEVERITY_MAP = {
  "HIGH": { label: "High", color: "red" },
  "CRITICAL": { label: "Critical", color: "red" },
  "MEDIUM": { label: "Medium", color: "orange" },
  "LOW": { label: "Low", color: "blue" }
};

// antd 3 has no "default" Tag preset - see the note on IGNORED below. Use this
// wherever a tag should render in antd's neutral style, and pass it through as
// `color={x || undefined}` so React omits the prop entirely.
const NEUTRAL_TAG_COLOR = undefined;

// The severity levels that get their own tile, in the order they are shown.
// Kept in step with SEVERITY_MAP; any severity outside this list is counted into
// stats.otherSeverity and surfaced rather than dropped.
const SEVERITY_TILE_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

// Workflow status mapping. These are DISPLAY LABELS ONLY. Every code except
// IGNORED comes straight from the API's `currentStatus`; a code that is not
// listed here is rendered verbatim, so an unmapped backend status stays visible
// instead of being silently relabelled. IGNORED is derived - see
// wasIgnoredClosure() below.
const STATUS_MAP = {
  "NEW": { label: "New", color: "blue" },
  "UNDER_ANALYSIS": { label: "Under Analysis", color: "orange" },
  "ACTION_PENDING": { label: "Action Pending", color: "gold" },
  "ACTION_TAKEN": { label: "Action Taken", color: "green" },
  // Ignored is deliberately neutral - it is a closure, but not a resolution.
  // NEUTRAL_TAG_COLOR (not "default") because antd 3's Tag has no "default"
  // preset: an unrecognised value makes it an inverse tag (white text) with an
  // invalid `background-color`, which renders as unreadable white-on-grey.
  // Passing no colour at all gives antd's own neutral tag: dark text, light
  // background, visible border.
  "IGNORED": { label: "Ignored", color: NEUTRAL_TAG_COLOR }
};

// Presentation order for the Status filter. Used only to sort the options that
// are actually present in the loaded data - it never adds an option for a
// status the data does not contain. Unknown codes sort to the end.
const STATUS_DISPLAY_ORDER = [
  "NEW",
  "UNDER_ANALYSIS",
  "ACTION_PENDING",
  "ACTION_TAKEN",
  "IGNORED"
];

// Which endpoint a row came from. This is the authoritative open/closed record:
// it is stamped from the response the alert arrived in, never inferred from
// `currentStatus`, which is a workflow stage and not a lifecycle flag.
const ALERT_LIFECYCLE = { OPEN: "OPEN", CLOSED: "CLOSED" };

const ALERT_SOURCES = [
  { lifecycle: ALERT_LIFECYCLE.CLOSED, path: "/alerts/closed", label: "closed" },
  { lifecycle: ALERT_LIFECYCLE.OPEN, path: "/alerts/non-closed", label: "open" }
];

const LIFECYCLE_MAP = {
  [ALERT_LIFECYCLE.OPEN]: { label: "Open", color: "volcano" },
  [ALERT_LIFECYCLE.CLOSED]: { label: "Closed", color: "green" }
};

// Ignore closures: the backend has NO `IGNORED` currentStatus. An alert closed
// with "Ignore" comes back as `currentStatus = ACTION_TAKEN`; the only record of
// the closure type is the terminal ACTION_TAKEN statusHistory entry, whose
// `remarks` field reads "Ignore" / "Ignored".
//
// That remark is SYSTEM-GENERATED - the backend writes it when the Ignore action
// is taken. It is not free text a user types, so the exact-match test below is
// reading a fixed marker, not interpreting prose.
//
// This is the same rule the AssetPro Ops Dashboard alert KPIs use
// (aiasl-ops-hub/src/services/alertsService.js -> wasIgnoredClosure), kept
// identical so the two screens never disagree about what "Ignored" means.
//
// Nothing is guessed: a closure counts as Ignored only when the remark is
// exactly "Ignore" or "Ignored" (case-insensitive, trimmed). Matching the whole
// remark rather than searching inside it keeps a user-authored remark that
// merely mentions the word from being read as an Ignore closure. The raw API
// value is preserved on every row (`currentStatus`) and exported in its own CSV
// column, so the transformation is always auditable against the source.
const IGNORE_REMARK_RE = /^ignore[d]?$/i;

// True when this alert's history encodes an "Ignore" closure. Only the LATEST
// ACTION_TAKEN entry is examined - that is the closure that produced the current
// status; earlier ones belong to superseded workflow passes.
const wasIgnoredClosure = (history) => {
  if (!Array.isArray(history)) return false;
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry && entry.status === "ACTION_TAKEN") {
      return IGNORE_REMARK_RE.test(String(entry.remarks || "").trim());
    }
  }
  return false;
};

// The status this report displays and filters on. Equal to the API's
// `currentStatus` in every case except an Ignore closure, which the API reports
// as ACTION_TAKEN. Returns null/undefined unchanged - a missing status is never
// turned into a real one.
const getEffectiveStatus = (alert) => {
  if (!alert) return null;
  const raw = alert.currentStatus;
  if (raw === "ACTION_TAKEN" && wasIgnoredClosure(alert.statusHistory)) {
    return "IGNORED";
  }
  return raw;
};

// Stable identity used to de-duplicate across the two endpoints. `_id` is the
// document key; `alertId` is the human-facing code and is used only when `_id`
// is absent. Returns null when neither exists - such a record cannot be matched
// to another and is kept as its own row.
const getAlertIdentity = (alert) => {
  if (!alert) return null;
  if (alert._id) return `id:${alert._id}`;
  if (alert.alertId) return `code:${alert.alertId}`;
  return null;
};

// Color mapping for alert types
const getAlertTypeColor = (alertType) => {
  const colorMap = {
    "FULL_TANK_MILEAGE_ALERT": { bg: "#fff4e6", color: "#d46b08" },
    "IOT_BASED_MILEAGE_ALERT": { bg: "#f6ffed", color: "#389e0d" },
    "DOCUMENT_RENEWAL_ALERT": { bg: "#e9f2ff", color: "#0747a6" },
    "ASSET_AUDIT_DUE_ALERT": { bg: "#fff0f6", color: "#c41d7f" },
    "MANPOWER_ENTRY_PENDING_ALERT": { bg: "#f0f5ff", color: "#2f54eb" }
  };
  
  return colorMap[alertType] || { bg: "#f4f5f7", color: "#42526e" };
};

// Helper to get readable alert type
const getAlertTypeLabel = (alertType) => {
  return ALERT_TYPE_MAP[alertType] || alertType;
};

// Helper to get severity info
const getSeverityInfo = (severity) => {
  return SEVERITY_MAP[severity] || { label: severity, color: NEUTRAL_TAG_COLOR };
};

const getSeverityTagStyle = () => undefined;

// Helper to get status info
const getStatusInfo = (status) => {
  return STATUS_MAP[status] || { label: status, color: NEUTRAL_TAG_COLOR };
};

// Helper to get lifecycle (open/closed) info
const getLifecycleInfo = (lifecycle) => {
  return LIFECYCLE_MAP[lifecycle] || { label: lifecycle || "Unknown", color: NEUTRAL_TAG_COLOR };
};

// Sort status codes for display, known codes first in workflow order.
const sortStatusCodes = (codes) =>
  [...codes].sort((a, b) => {
    const ai = STATUS_DISPLAY_ORDER.indexOf(a);
    const bi = STATUS_DISPLAY_ORDER.indexOf(b);
    return (
      (ai === -1 ? STATUS_DISPLAY_ORDER.length : ai) -
      (bi === -1 ? STATUS_DISPLAY_ORDER.length : bi)
    );
  });

// Helper to extract asset info from alert
const extractAssetInfo = (alert) => {
  const asset = alert.asset || alert.entity || {};
  const assetModel = asset.assetModel || {};
  const location = asset.location || {};
  const client = asset.client || {};
  const equipmentSubType = asset.equipmentSubType || {};
  
  return {
    assetCode: asset.assetCode || "N/A",
    altName: asset.altName || "",
    ownerCode: asset.ownerCode || "",
    location: location.name || location.code || "N/A",
    subLocation: client.name || client.code || "",
    assetModel: assetModel.name || "",
    assetSubType: equipmentSubType.name || ""
  };
};

// Helper to extract workflow info (analysis, action suggested, action taken)
const extractWorkflowInfo = (alert) => {
  const statusHistory = alert.statusHistory || [];
  let analysis = "";
  let actionSuggested = "";
  let actionTaken = "";
  let closedBy = null;
  let closedAt = null;
  
  // Find the latest analysisId with workflow info
  for (let i = statusHistory.length - 1; i >= 0; i--) {
    const entry = statusHistory[i];
    if (entry.analysisId) {
      if (!analysis && entry.analysisId.analysis) {
        analysis = entry.analysisId.analysis;
      }
      if (!actionSuggested && entry.analysisId.actionSuggested) {
        actionSuggested = entry.analysisId.actionSuggested;
      }
      if (!actionTaken && entry.analysisId.actionTaken) {
        actionTaken = entry.analysisId.actionTaken;
      }
    }
    // Get closed by info from ACTION_TAKEN status
    if (entry.status === "ACTION_TAKEN" && entry.updatedBy) {
      closedBy = entry.updatedBy;
      closedAt = entry.date;
    }
  }
  
  return { analysis, actionSuggested, actionTaken, closedBy, closedAt };
};

// Resolve the alert's CREATION timestamp (epoch ms).
// `alertDateTime` is the backend's authoritative creation field (epoch ms) and
// is what the server filters on; `createdAt` is the ISO document timestamp and
// is used only when `alertDateTime` is absent. Returns null when neither is
// present — the caller must flag that record rather than assume a date.
const getCreatedMs = (alert) => {
  const alertDateTime = Number(alert.alertDateTime);
  if (Number.isFinite(alertDateTime) && alertDateTime > 0) return alertDateTime;
  const parsed = Date.parse(alert.createdAt);
  return Number.isFinite(parsed) ? parsed : null;
};

// Convert the picked [start, end] days into the half-open epoch-ms window the
// API expects: from = start of the start day, to = start of the day AFTER the
// end day. `to` is exclusive, so the end day is fully included.
//
// API contract (see assetpro-knowledge / API_SWAGGER_DISCOVERY.md):
//   GET /alerts/closed?from=<epochMs>&to=<epochMs>      -> resolved alerts
//   GET /alerts/non-closed?from=<epochMs>&to=<epochMs>  -> still-open alerts
// Both endpoints are queried for the same window so the report covers every
// workflow status, not just the resolved ones.
// The server filters on the alert's CREATION time (alertDateTime), NOT on the
// closure time. That is exactly what this report's date field means.
const buildCreatedRangeMs = (range) => {
  if (!range || range.length !== 2 || !range[0] || !range[1]) return null;
  const fromMs = moment(range[0]).startOf("day").valueOf();
  const toMs = moment(range[1]).startOf("day").add(1, "day").valueOf();
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return null;
  return { fromMs, toMs };
};

// Summary counts for a set of rows. Called with whatever the table is currently
// showing, so the tiles and the Status Breakdown always describe exactly the rows
// on screen rather than the whole loaded period. Every bucket is a plain count of
// records actually present - nothing is estimated or carried over from a previous
// set.
const computeStats = (rows) => {
  const stats = {
    total: rows.length,
    byType: {},
    bySeverity: {},
    byStatus: {},
    byLifecycle: {},
    // Alerts the API returned with no currentStatus. Counted on their own rather
    // than folded into any status bucket.
    missingStatus: 0,
    // Alerts whose severity is absent or outside the four known levels. Surfaced
    // separately so the four severity tiles can never silently under-count.
    otherSeverity: 0
  };

  rows.forEach(alert => {
    const type = alert.alertType || "Unknown";
    stats.byType[type] = (stats.byType[type] || 0) + 1;

    const severity = alert.alertSeverity || "Unknown";
    stats.bySeverity[severity] = (stats.bySeverity[severity] || 0) + 1;
    if (!SEVERITY_TILE_ORDER.includes(severity)) stats.otherSeverity += 1;

    // Effective status: Ignore closures counted as IGNORED.
    if (alert._status) {
      stats.byStatus[alert._status] = (stats.byStatus[alert._status] || 0) + 1;
    } else {
      stats.missingStatus += 1;
    }

    // Which endpoint returned it.
    stats.byLifecycle[alert._lifecycle] = (stats.byLifecycle[alert._lifecycle] || 0) + 1;
  });

  return stats;
};

const formatRangeLabel = (range) =>
  range && range.length === 2
    ? `${moment(range[0]).format("DD-MM-YYYY")} to ${moment(range[1]).format("DD-MM-YYYY")}`
    : "";

export default class ClosedAlertsReport extends React.Component {
  state = {
    loading: false,
    // Range currently selected in the picker (may differ from what is loaded).
    createdRange: null,
    // Range the currently displayed data was actually fetched for. Null until
    // the first successful load — the report never auto-fetches.
    loadedRange: null,
    hasLoaded: false,
    // Data-quality counters for the loaded set (see validateCreatedDates).
    dataIssues: { outOfRange: 0, missingCreatedAt: 0 },
    // Outcome of the two lifecycle queries for the loaded set. `failedSources`
    // is non-empty when one endpoint failed and the report is showing a partial
    // period - that must be visible, never silently absorbed.
    loadIssues: { failedSources: [], duplicatesAcrossSets: 0 },
    data: [],
    filteredData: [],
    filters: {
      search: "",
      alertType: null,
      severity: null,
      status: null,
      lifecycle: null,
      location: null,
      closedAtDateRange: null
    },
    drawerVisible: false,
    selectedAlert: null,
    // Unique filter options extracted from data
    alertTypes: [],
    severities: [],
    locations: [],
    statuses: [],
    // Stats
    stats: {
      total: 0,
      byType: {},
      bySeverity: {},
      byStatus: {},
      byLifecycle: {},
      missingStatus: 0,
      otherSeverity: 0
    }
  };

  // No auto-fetch on mount: alerts are pulled only for an explicitly selected
  // created-date range (the API scopes the query server-side).

  // Count records whose creation date does not fall inside the requested
  // window, and records with no creation date at all. Nothing is dropped —
  // every row the API returns is shown; discrepancies are surfaced as a
  // warning so a reviewer can see them instead of them being hidden.
  validateCreatedDates = (data, fromMs, toMs) => {
    let outOfRange = 0;
    let missingCreatedAt = 0;

    data.forEach(alert => {
      const createdMs = getCreatedMs(alert);
      if (createdMs === null) {
        missingCreatedAt += 1;
      } else if (createdMs < fromMs || createdMs >= toMs) {
        outOfRange += 1;
      }
    });

    return { outOfRange, missingCreatedAt };
  };

  loadAlerts = () => {
    const { createdRange } = this.state;
    if (!buildCreatedRangeMs(createdRange)) {
      message.warning("Select a created date range before loading alerts");
      return;
    }
    this.fetchAlerts(createdRange);
  };

  refreshAlerts = () => {
    const { loadedRange } = this.state;
    if (!loadedRange) {
      message.warning("Select a created date range and load alerts first");
      return;
    }
    this.fetchAlerts(loadedRange);
  };

  fetchAlerts = async (range) => {
    const rangeMs = buildCreatedRangeMs(range);

    // Guard: never fall back to an unscoped fetch. An unscoped call would pull
    // the entire alert history and silently misrepresent the selected period.
    if (!rangeMs) {
      message.error("Invalid created date range - alerts were not loaded");
      return;
    }

    const { fromMs, toMs } = rangeMs;
    this.setState({ loading: true });

    try {
      // Both lifecycle endpoints, same window, issued together - one request
      // each per Load/Refresh click, never per row. They are independent
      // queries: one failing must not discard the other's results, but it must
      // not be hidden either, or a partial list would read as the complete
      // period. Each source's outcome is tracked and surfaced.
      const responses = await Promise.all(
        ALERT_SOURCES.map(source =>
          apiGet(`${source.path}?from=${fromMs}&to=${toMs}`)
            .then(response => ({ source, response }))
            .catch(error => ({ source, response: { success: false, error: error.message } }))
        )
      );

      const failedSources = [];
      const loadedSources = [];
      const collected = [];

      responses.forEach(({ source, response }) => {
        if (!response || !response.success || !Array.isArray(response.data)) {
          failedSources.push({
            label: source.label,
            error: (response && response.error) || "request failed"
          });
          return;
        }
        loadedSources.push(source.label);
        // Stamp the lifecycle from the endpoint that returned the record, and
        // resolve the effective status once here rather than on every render.
        // `currentStatus` is left untouched so the raw API value stays available.
        response.data.forEach(alert =>
          collected.push({
            ...alert,
            _lifecycle: source.lifecycle,
            _status: getEffectiveStatus(alert)
          })
        );
      });

      // Both endpoints down: there is nothing to show and no honest partial
      // view, so this is an error rather than an empty result set.
      if (loadedSources.length === 0) {
        message.error(
          `Failed to fetch alerts: ${failedSources
            .map(f => `${f.label} alerts (${f.error})`)
            .join("; ")}`
        );
        this.setState({ loading: false });
        return;
      }

      // An alert closed between the two requests can come back from both
      // endpoints. Keep the CLOSED copy - it is the later state - and count the
      // collision instead of letting the row appear twice.
      const seenIdentities = new Map();
      const data = [];
      let duplicatesAcrossSets = 0;

      collected.forEach(alert => {
        const identity = getAlertIdentity(alert);
        if (identity !== null && seenIdentities.has(identity)) {
          duplicatesAcrossSets += 1;
          const index = seenIdentities.get(identity);
          if (alert._lifecycle === ALERT_LIFECYCLE.CLOSED) data[index] = alert;
          return;
        }
        if (identity !== null) seenIdentities.set(identity, data.length);
        data.push(alert);
      });

      // Extract unique filter options. Every option list comes from the loaded
      // data, so a filter can never offer a value the report does not hold.
      const alertTypes = [...new Set(data.map(a => a.alertType).filter(Boolean))];
      const severities = [...new Set(data.map(a => a.alertSeverity).filter(Boolean))];
      const locations = [...new Set(data.map(a => {
        const asset = a.asset || a.entity || {};
        return asset.location?.name || asset.location?.code;
      }).filter(Boolean))];
      const statuses = sortStatusCodes(
        [...new Set(data.map(a => a._status).filter(Boolean))]
      );

      this.setState(
        {
          data,
          alertTypes,
          severities,
          locations,
          statuses,
          loadedRange: range,
          hasLoaded: true,
          dataIssues: this.validateCreatedDates(data, fromMs, toMs),
          loadIssues: { failedSources, duplicatesAcrossSets },
          loading: false
        },
        // Re-apply the active client-side filters to the new result set.
        this.applyFilters
      );
    } catch (error) {
      console.error("Error fetching alerts:", error);
      message.error("Failed to fetch alerts");
      this.setState({ loading: false });
    }
  };

  applyFilters = () => {
    const { data, filters } = this.state;
    
    let filtered = [...data];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(alert => {
        const assetInfo = extractAssetInfo(alert);
        const searchFields = [
          alert.alertId,
          alert.alertTitle,
          assetInfo.assetCode,
          assetInfo.altName,
          assetInfo.ownerCode,
          assetInfo.location,
          assetInfo.subLocation,
          assetInfo.assetModel
        ].filter(Boolean).join(" ").toLowerCase();
        
        return searchFields.includes(searchLower);
      });
    }
    
    // Alert type filter
    if (filters.alertType) {
      filtered = filtered.filter(alert => alert.alertType === filters.alertType);
    }
    
    // Severity filter
    if (filters.severity) {
      filtered = filtered.filter(alert => alert.alertSeverity === filters.severity);
    }
    
    // Workflow status filter (exact match on the effective status code)
    if (filters.status) {
      filtered = filtered.filter(alert => alert._status === filters.status);
    }

    // Lifecycle filter (open/closed, from the endpoint the row came from)
    if (filters.lifecycle) {
      filtered = filtered.filter(alert => alert._lifecycle === filters.lifecycle);
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(alert => {
        const asset = alert.asset || alert.entity || {};
        const location = asset.location?.name || asset.location?.code;
        return location === filters.location;
      });
    }
    
    // NOTE: there is no client-side created-date filter any more. The created
    // date range is applied server-side by /alerts/closed?from=&to=, so the
    // loaded set already covers exactly the requested period.

    // Closed At date range filter
    // Start date = start of day (00:00:00), End date = end of day (23:59:59.999)
    // Alerts with no closure date - every still-open alert, plus any closed
    // alert whose history carries no ACTION_TAKEN entry - cannot satisfy a
    // closure-date window and are excluded while this filter is set. The filter
    // label states this so an unexpectedly short list is never a mystery.
    if (filters.closedAtDateRange && filters.closedAtDateRange.length === 2) {
      const [startDate, endDate] = filters.closedAtDateRange;
      const rangeStart = moment(startDate).startOf("day");
      const rangeEnd = moment(endDate).endOf("day");
      filtered = filtered.filter(alert => {
        const workflowInfo = extractWorkflowInfo(alert);
        const closedAt = workflowInfo.closedAt;
        if (!closedAt) return false;
        const closedAtMoment = moment(closedAt);
        return closedAtMoment.isSameOrAfter(rangeStart) && closedAtMoment.isSameOrBefore(rangeEnd);
      });
    }
    
    // Stats follow the visible rows, so every tile and the Status Breakdown
    // describe the current filter selection rather than the whole loaded period.
    this.setState({ filteredData: filtered, stats: computeStats(filtered) });
  };

  setFilter = (key, value) => {
    this.setState(
      prevState => ({
        filters: {
          ...prevState.filters,
          [key]: value
        }
      }),
      this.applyFilters
    );
  };

  // Resets the client-side filters only. The created date range is the query
  // scope, not a filter, so it is deliberately left untouched here.
  clearFilters = () => {
    this.setState(
      {
        filters: {
          search: "",
          alertType: null,
          severity: null,
          status: null,
          lifecycle: null,
          location: null,
          closedAtDateRange: null
        }
      },
      // Re-run the (now empty) filter set rather than assigning the rows
      // directly, so filteredData and stats are always produced by the same code
      // path and cannot drift apart.
      this.applyFilters
    );
  };

  openDrawer = (alert) => {
    this.setState({
      drawerVisible: true,
      selectedAlert: alert
    });
  };

  closeDrawer = () => {
    this.setState({
      drawerVisible: false,
      selectedAlert: null
    });
  };

  exportToCSV = () => {
    const { filteredData, loadedRange } = this.state;

    if (filteredData.length === 0) {
      message.warning("No data to export");
      return;
    }
    
    // Prepare CSV data
    const headers = [
      "Alert ID",
      "Alert Type",
      "Severity",
      "Title",
      "Asset Code",
      "Alt Name",
      "Owner Code",
      "Location",
      "Sub Location",
      "Asset Model",
      "Asset Sub Type",
      "Assigned To",
      "Lifecycle",
      "Status",
      "Status (API currentStatus)",
      "Analysis",
      "Action Suggested",
      "Action Taken",
      "Closed By",
      "Closed At",
      "Created At",
      "Updated At"
    ];
    
    const rows = filteredData.map(alert => {
      const assetInfo = extractAssetInfo(alert);
      const workflowInfo = extractWorkflowInfo(alert);
      
      return [
        alert.alertId || "",
        getAlertTypeLabel(alert.alertType),
        getSeverityInfo(alert.alertSeverity).label,
        (alert.alertTitle || "").replace(/"/g, '""'),
        assetInfo.assetCode,
        assetInfo.altName,
        assetInfo.ownerCode,
        assetInfo.location,
        assetInfo.subLocation,
        assetInfo.assetModel,
        assetInfo.assetSubType,
        alert.assignedTo?.name || "Unassigned",
        getLifecycleInfo(alert._lifecycle).label,
        // Effective status (Ignore closures resolved to Ignored). Blank means
        // the record carried no status, which is not the same as any status
        // value.
        alert._status ? getStatusInfo(alert._status).label : "",
        // The raw API value, preserved so every derived status in the column
        // above can be audited against the source record.
        alert.currentStatus || "",
        (workflowInfo.analysis || "").replace(/"/g, '""'),
        (workflowInfo.actionSuggested || "").replace(/"/g, '""'),
        (workflowInfo.actionTaken || "").replace(/"/g, '""'),
        workflowInfo.closedBy?.name || "",
        workflowInfo.closedAt ? moment(workflowInfo.closedAt).format("DD-MM-YYYY HH:mm:ss") : "",
        alert.createdAt ? moment(alert.createdAt).format("DD-MM-YYYY HH:mm:ss") : "",
        alert.updatedAt ? moment(alert.updatedAt).format("DD-MM-YYYY HH:mm:ss") : ""
      ];
    });
    
    // Build CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    // Filename records the created-date range the data was pulled for, so an
    // exported file is never ambiguous about the period it covers.
    const rangeSuffix = loadedRange
      ? `${moment(loadedRange[0]).format("YYYY-MM-DD")}_to_${moment(loadedRange[1]).format("YYYY-MM-DD")}`
      : "no-range";
    link.setAttribute(
      "download",
      `Alerts_Report_created_${rangeSuffix}_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Exported ${filteredData.length} records to CSV`);
  };

  // Table columns
  getColumns = () => [
    {
      title: "Alert ID",
      dataIndex: "alertId",
      key: "alertId",
      width: 100,
      fixed: "left",
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: "auto" }}
          onClick={() => this.openDrawer(record)}
        >
          {text}
        </Button>
      )
    },
    {
      title: "Type",
      dataIndex: "alertType",
      key: "alertType",
      width: 180,
      render: (alertType) => {
        const typeColor = getAlertTypeColor(alertType);
        return (
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 999,
              background: typeColor.bg,
              color: typeColor.color,
              fontWeight: 600,
              whiteSpace: "nowrap"
            }}
          >
            {getAlertTypeLabel(alertType)}
          </span>
        );
      }
    },
    {
      title: "Severity",
      dataIndex: "alertSeverity",
      key: "alertSeverity",
      width: 100,
      align: "center",
      render: (severity) => {
        const info = getSeverityInfo(severity);
        return <Tag color={info.color} style={getSeverityTagStyle(info)}>{info.label.toUpperCase()}</Tag>;
      }
    },
    {
      title: "Status",
      dataIndex: "_status",
      key: "status",
      width: 150,
      render: (status, record) => {
        // Two distinct facts, shown together: `_lifecycle` is the endpoint the
        // row came from (open vs closed), `_status` is the workflow stage.
        // Neither is derived from the other.
        const lifecycleInfo = getLifecycleInfo(record._lifecycle);
        // An Ignored row is a derived reading of an ACTION_TAKEN record; say so
        // on hover so the displayed status can always be traced to the source.
        const derived = status === "IGNORED" && record.currentStatus === "ACTION_TAKEN";
        return (
          <div>
            {status ? (
              <Tooltip
                title={
                  derived
                    ? 'API status: ACTION_TAKEN, closed with the remark "Ignore"'
                    : null
                }
              >
                <Tag color={getStatusInfo(status).color}>
                  {getStatusInfo(status).label}
                  {derived && <span style={{ marginLeft: 4 }}>*</span>}
                </Tag>
              </Tooltip>
            ) : (
              <Tooltip title="The API returned no status for this alert">
                <Tag>No status</Tag>
              </Tooltip>
            )}
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
              {lifecycleInfo.label}
            </div>
          </div>
        );
      }
    },
    {
      title: "Title",
      dataIndex: "alertTitle",
      key: "alertTitle",
      width: 300,
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => this.openDrawer(record)}
          >
            {text}
          </span>
        </Tooltip>
      )
    },
    {
      title: "Asset",
      key: "asset",
      width: 200,
      render: (_, record) => {
        const assetInfo = extractAssetInfo(record);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{assetInfo.assetCode}</div>
            {assetInfo.altName && (
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>{assetInfo.altName}</div>
            )}
          </div>
        );
      }
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      render: (_, record) => {
        const assetInfo = extractAssetInfo(record);
        return (
          <div>
            <div>{assetInfo.location}</div>
            {assetInfo.subLocation && (
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>{assetInfo.subLocation}</div>
            )}
          </div>
        );
      }
    },
    {
      title: "Assigned To",
      key: "assignedTo",
      width: 150,
      render: (_, record) => record.assignedTo?.name || <Tag>Unassigned</Tag>
    },
    {
      title: "Action Taken",
      key: "actionTaken",
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const workflowInfo = extractWorkflowInfo(record);
        return (
          <Tooltip title={workflowInfo.actionTaken || workflowInfo.actionSuggested || "N/A"}>
            <span style={{ color: workflowInfo.actionTaken ? "#389e0d" : "#8c8c8c" }}>
              {workflowInfo.actionTaken || workflowInfo.actionSuggested || "N/A"}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: "Closed By",
      key: "closedBy",
      width: 150,
      render: (_, record) => {
        // "Not closed" (still open) and "N/A" (closed, but no closer recorded)
        // are different facts and are never collapsed into one another.
        if (record._lifecycle === ALERT_LIFECYCLE.OPEN) {
          return <Text type="secondary">Not closed</Text>;
        }
        const workflowInfo = extractWorkflowInfo(record);
        return workflowInfo.closedBy?.name || "N/A";
      }
    },
    {
      title: "Closed At",
      key: "closedAt",
      width: 150,
      render: (_, record) => {
        if (record._lifecycle === ALERT_LIFECYCLE.OPEN) {
          return <Text type="secondary">Not closed</Text>;
        }
        const workflowInfo = extractWorkflowInfo(record);
        return workflowInfo.closedAt 
          ? moment(workflowInfo.closedAt).format("DD MMM YYYY, HH:mm")
          : "N/A";
      },
      sorter: (a, b) => {
        const aInfo = extractWorkflowInfo(a);
        const bInfo = extractWorkflowInfo(b);
        return moment(aInfo.closedAt || 0).unix() - moment(bInfo.closedAt || 0).unix();
      },
      defaultSortOrder: "descend"
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date) => date ? moment(date).format("DD MMM YYYY, HH:mm") : "N/A",
      sorter: (a, b) => moment(a.createdAt || 0).unix() - moment(b.createdAt || 0).unix()
    }
  ];

  renderStatsCards = () => {
    const { stats, loading, statuses, data, filters } = this.state;

    // Whether any client-side filter is narrowing the loaded set. Drives the
    // scope line below - the tiles are counts of the visible rows, so what they
    // cover has to be stated, never left to be assumed.
    const filtersActive = Boolean(
      filters.search ||
      filters.alertType ||
      filters.severity ||
      filters.status ||
      filters.lifecycle ||
      filters.location ||
      (filters.closedAtDateRange && filters.closedAtDateRange.length === 2)
    );

    // Colour per severity, taken from SEVERITY_MAP so the tiles and the table's
    // severity tags cannot drift apart.
    const severityTileColor = {
      CRITICAL: "#a8071a",
      HIGH: "#cf1322",
      MEDIUM: "#faad14",
      LOW: "#1890ff"
    };
    const severityTileIcon = {
      CRITICAL: "fire",
      HIGH: "warning",
      MEDIUM: "exclamation-circle",
      LOW: "info-circle"
    };

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {filtersActive ? (
              <span>
                Figures below cover the <strong>{stats.total}</strong> alert(s)
                matching the current filters, of <strong>{data.length}</strong>{" "}
                loaded for the selected date range.
              </span>
            ) : (
              <span>
                Figures below cover all <strong>{data.length}</strong> alert(s)
                loaded for the selected date range.
              </span>
            )}
          </Text>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card size="small">
            <Statistic
              title={filtersActive ? "Alerts (filtered)" : "Total Alerts"}
              value={stats.total}
              loading={loading}
              prefix={<Icon type="profile" style={{ color: "#1890ff" }} />}
            />
            {/* Open/closed counts come from the endpoint each row was returned
                by, so they always add up to the total. Hidden while a load is in
                flight so the previous period's split is never read as this one's. */}
            {!loading && (
              <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                {stats.byLifecycle[ALERT_LIFECYCLE.OPEN] || 0} open ·{" "}
                {stats.byLifecycle[ALERT_LIFECYCLE.CLOSED] || 0} closed
              </div>
            )}
          </Card>
        </Col>

        {/* One tile per severity level. All four are always shown - a level with
            no matching alerts reads 0, which is a real count of the visible rows,
            not a placeholder for missing data. */}
        {SEVERITY_TILE_ORDER.map(code => (
          <Col key={code} xs={12} sm={12} md={4} lg={5}>
            <Card size="small">
              <Statistic
                title={getSeverityInfo(code).label}
                value={stats.bySeverity[code] || 0}
                loading={loading}
                valueStyle={{ color: severityTileColor[code] }}
                prefix={<Icon type={severityTileIcon[code]} />}
              />
            </Card>
          </Col>
        ))}

        {/* Severities outside the four known levels, and alerts with none at
            all, would otherwise be invisible between the tiles and the total. */}
        {!loading && stats.otherSeverity > 0 && (
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              message={`${stats.otherSeverity} of ${stats.total} alert(s) have a severity outside Critical / High / Medium / Low, or none at all. They are counted in the total and shown in the table, but not in the four tiles above.`}
            />
          </Col>
        )}

        {/* Status breakdown of the visible rows. Every status present in the
            loaded period is listed so a filtered-out status reads 0 rather than
            disappearing; the scope line above says which set the counts cover. */}
        <Col xs={24}>
          <Card size="small" title="Status Breakdown">
            {statuses.length === 0 && stats.missingStatus === 0 ? (
              <Text type="secondary">No status values in the loaded alerts</Text>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {statuses.map(code => {
                  const info = getStatusInfo(code);
                  return (
                    <Tag key={code} color={info.color} style={{ marginRight: 0 }}>
                      {info.label}: <strong>{stats.byStatus[code] || 0}</strong>
                    </Tag>
                  );
                })}
                {stats.missingStatus > 0 && (
                  <Tooltip title="The API returned these alerts with no currentStatus value">
                    <Tag color="red" style={{ marginRight: 0 }}>
                      No status: <strong>{stats.missingStatus}</strong>
                    </Tag>
                  </Tooltip>
                )}
              </div>
            )}
            {/* Ignored is the one derived status - state where it comes from
                wherever its count is shown. */}
            {statuses.includes("IGNORED") && (
              <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 8 }}>
                Ignored alerts are reported by the API as ACTION_TAKEN and identified
                by the closure remark "Ignore". The raw API status is kept on every
                row and exported in its own CSV column.
              </div>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  // Query scope for the report. Alerts are pulled from the API only for the
  // created-date range chosen here (server-side filter on alert creation time).
  renderQueryBar = () => {
    const { createdRange, loadedRange, loading, hasLoaded, data, dataIssues, loadIssues } = this.state;

    const rangeMs = buildCreatedRangeMs(createdRange);
    const rangeChanged =
      hasLoaded && formatRangeLabel(createdRange) !== formatRangeLabel(loadedRange);

    const labelStyle = {
      display: "block",
      marginBottom: 6,
      fontSize: 12,
      fontWeight: 500,
      color: "#595959"
    };

    return (
      <Card
        size="small"
        title={
          <span>
            <Icon type="calendar" style={{ marginRight: 8 }} />
            Alert Created Date Range
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[24, 16]} type="flex" align="bottom">
          <Col xs={24} sm={16} md={12} lg={9}>
            <label style={labelStyle}>Created Date Range (required)</label>
            <RangePicker
              value={createdRange}
              onChange={(dates) =>
                this.setState({ createdRange: dates && dates.length === 2 ? dates : null })
              }
              format="DD-MM-YYYY"
              placeholder={["Start date", "End date"]}
              disabledDate={(current) => current && current.isAfter(moment().endOf("day"))}
              ranges={{
                "Last 7 Days": [moment().subtract(6, "days"), moment()],
                "Last 30 Days": [moment().subtract(29, "days"), moment()],
                "This Month": [moment().startOf("month"), moment()],
                "Last Month": [
                  moment().subtract(1, "month").startOf("month"),
                  moment().subtract(1, "month").endOf("month")
                ]
              }}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Button
              type="primary"
              icon="search"
              loading={loading}
              disabled={!rangeMs}
              onClick={this.loadAlerts}
              style={{ width: "100%" }}
            >
              Load Alerts
            </Button>
          </Col>
          <Col xs={24} md={24} lg={11}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {hasLoaded ? (
                <span>
                  Showing <strong>{data.length}</strong> alert(s) created between{" "}
                  <strong>{formatRangeLabel(loadedRange)}</strong>
                  {rangeChanged && (
                    <span style={{ color: "#d46b08" }}>
                      {" "}
                      — date range changed, click "Load Alerts" to refresh
                    </span>
                  )}
                </span>
              ) : (
                "Select a created date range and click \"Load Alerts\". Open and closed alerts are both loaded. The date range applies to the alert's created date, not its closed date."
              )}
            </Text>
          </Col>
        </Row>

        {/* Data-quality warnings. Nothing is excluded from the table - these
            counts simply make discrepancies in the API response visible. */}
        {hasLoaded && loadIssues.failedSources.length > 0 && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 12 }}
            message={
              `Only part of the period could be loaded: ` +
              loadIssues.failedSources
                .map(f => `${f.label} alerts failed (${f.error})`)
                .join("; ") +
              `. The figures below cover the endpoints that responded only - do not read them as the complete period.`
            }
          />
        )}
        {hasLoaded && loadIssues.duplicatesAcrossSets > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={`${loadIssues.duplicatesAcrossSets} alert(s) were returned by both the open and closed queries (closed between the two requests). The closed record was kept for each.`}
          />
        )}
        {hasLoaded && dataIssues.outOfRange > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={`${dataIssues.outOfRange} of ${data.length} alert(s) returned by the API have a created date outside the requested range. They are still shown below - verify before using this report.`}
          />
        )}
        {hasLoaded && dataIssues.missingCreatedAt > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message={`${dataIssues.missingCreatedAt} of ${data.length} alert(s) have no created date. They are shown below but could not be verified against the selected range.`}
          />
        )}
      </Card>
    );
  };

  renderFilters = () => {
    const { filters, alertTypes, severities, locations, statuses, loading, loadedRange } = this.state;
    
    const filterLabelStyle = {
      display: "block",
      marginBottom: 6,
      fontSize: 12,
      fontWeight: 500,
      color: "#595959"
    };
    
    return (
      <Card 
        size="small" 
        title={
          <span>
            <Icon type="filter" style={{ marginRight: 8 }} />
            Filters
          </span>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={6} lg={5}>
            <label style={filterLabelStyle}>Search</label>
            <Input
              value={filters.search}
              onChange={(e) => this.setFilter("search", e.target.value)}
              placeholder="Alerts, assets, locations..."
              prefix={<Icon type="search" />}
              allowClear
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <label style={filterLabelStyle}>Alert Type</label>
            <Select
              value={filters.alertType}
              onChange={(value) => this.setFilter("alertType", value)}
              placeholder="Select alert type"
              allowClear
              style={{ width: "100%" }}
            >
              {alertTypes.map(type => (
                <Option key={type} value={type}>
                  {getAlertTypeLabel(type)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3}>
            <label style={filterLabelStyle}>Severity</label>
            <Select
              value={filters.severity}
              onChange={(value) => this.setFilter("severity", value)}
              placeholder="Select severity"
              allowClear
              style={{ width: "100%" }}
            >
              {severities.map(sev => (
                <Option key={sev} value={sev}>
                  {getSeverityInfo(sev).label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <label style={filterLabelStyle}>Status</label>
            <Select
              value={filters.status}
              onChange={(value) => this.setFilter("status", value)}
              placeholder="Select status"
              allowClear
              style={{ width: "100%" }}
            >
              {/* Options come from the loaded data only. A status that no
                  loaded alert holds is not offered. */}
              {statuses.map(code => (
                <Option key={code} value={code}>
                  {getStatusInfo(code).label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={3}>
            <label style={filterLabelStyle}>Open / Closed</label>
            <Select
              value={filters.lifecycle}
              onChange={(value) => this.setFilter("lifecycle", value)}
              placeholder="All"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value={ALERT_LIFECYCLE.OPEN}>Open only</Option>
              <Option value={ALERT_LIFECYCLE.CLOSED}>Closed only</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={5}>
            <label style={filterLabelStyle}>Location</label>
            <Select
              value={filters.location}
              onChange={(value) => this.setFilter("location", value)}
              placeholder="Select location"
              allowClear
              style={{ width: "100%" }}
            >
              {locations.map(loc => (
                <Option key={loc} value={loc}>
                  {loc}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={12} lg={6}>
            <label style={filterLabelStyle}>
              Closed At (Date Range){" "}
              <Tooltip title="Alerts with no closure date - every open alert - are excluded while this filter is set.">
                <Icon type="info-circle" style={{ color: "#8c8c8c" }} />
              </Tooltip>
            </label>
            <RangePicker
              value={filters.closedAtDateRange}
              onChange={(dates) => this.setFilter("closedAtDateRange", dates)}
              format="DD-MM-YYYY"
              placeholder={["Start date", "End date"]}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Divider style={{ margin: "12px 0" }} />
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button
                icon="reload"
                onClick={this.clearFilters}
              >
                Reset Filters
              </Button>
              <Tooltip title={
                loadedRange
                  ? `Re-fetch alerts created between ${formatRangeLabel(loadedRange)}`
                  : "Load alerts for a created date range first"
              }>
                <Button
                  icon="sync"
                  onClick={this.refreshAlerts}
                  loading={loading}
                  disabled={!loadedRange}
                >
                  Refresh Data
                </Button>
              </Tooltip>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  renderDrawer = () => {
    const { drawerVisible, selectedAlert } = this.state;
    
    if (!selectedAlert) return null;
    
    const assetInfo = extractAssetInfo(selectedAlert);
    const workflowInfo = extractWorkflowInfo(selectedAlert);
    const statusHistory = selectedAlert.statusHistory || [];
    // Index of the closure entry that produced the current status - the same
    // entry wasIgnoredClosure() inspects.
    const lastActionTakenIndex = statusHistory
      .map(entry => entry && entry.status)
      .lastIndexOf("ACTION_TAKEN");
    
    return (
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>
              {selectedAlert.alertId}
            </span>
            <Tag
              color={getSeverityInfo(selectedAlert.alertSeverity).color}
              style={getSeverityTagStyle(getSeverityInfo(selectedAlert.alertSeverity))}
            >
              {getSeverityInfo(selectedAlert.alertSeverity).label.toUpperCase()}
            </Tag>
            {/* From the endpoint this alert was returned by, not inferred
                from its workflow status. */}
            <Tag color={getLifecycleInfo(selectedAlert._lifecycle).color}>
              {getLifecycleInfo(selectedAlert._lifecycle).label.toUpperCase()}
            </Tag>
          </div>
        }
        placement="right"
        closable={true}
        onClose={this.closeDrawer}
        visible={drawerVisible}
        width={700}
        zIndex={1400}
        bodyStyle={{ padding: 24 }}
      >
        {/* Alert Type Badge */}
        <div style={{ marginBottom: 16 }}>
          {(() => {
            const typeColor = getAlertTypeColor(selectedAlert.alertType);
            return (
              <span
                style={{
                  fontSize: 12,
                  padding: "4px 12px",
                  borderRadius: 999,
                  background: typeColor.bg,
                  color: typeColor.color,
                  fontWeight: 600
                }}
              >
                {getAlertTypeLabel(selectedAlert.alertType)}
              </span>
            );
          })()}
        </div>

        {/* Title */}
        <Title level={4} style={{ marginBottom: 16 }}>
          {selectedAlert.alertTitle}
        </Title>

        {/* Alert Details */}
        <Card size="small" title="Alert Details" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Alert ID">{selectedAlert.alertId}</Descriptions.Item>
            <Descriptions.Item label="Status">
              {selectedAlert._status ? (
                <span>
                  <Tag color={getStatusInfo(selectedAlert._status).color}>
                    {getStatusInfo(selectedAlert._status).label}
                  </Tag>
                  {/* Ignored is derived from the closure remark, so the raw API
                      value is stated here rather than replaced. */}
                  {selectedAlert._status === "IGNORED" &&
                    selectedAlert.currentStatus === "ACTION_TAKEN" && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        API: ACTION_TAKEN, closed with remark "Ignore"
                      </Text>
                    )}
                </span>
              ) : (
                <Tag>No status returned</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Asset Code">{assetInfo.assetCode}</Descriptions.Item>
            <Descriptions.Item label="Alt Name">{assetInfo.altName || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Owner Code">{assetInfo.ownerCode || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Asset Model">{assetInfo.assetModel || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Location">{assetInfo.location}</Descriptions.Item>
            <Descriptions.Item label="Sub Location">{assetInfo.subLocation || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Assigned To">
              {selectedAlert.assignedTo?.name || "Unassigned"}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedAlert.createdAt 
                ? moment(selectedAlert.createdAt).format("DD MMM YYYY, HH:mm:ss")
                : "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Alert Description */}
        {selectedAlert.alertDescription && (
          <Card size="small" title="Alert Description" style={{ marginBottom: 16 }}>
            <div
              style={{
                maxHeight: 300,
                overflow: "auto",
                fontSize: 13,
                lineHeight: 1.6
              }}
              dangerouslySetInnerHTML={{ __html: selectedAlert.alertDescription }}
            />
          </Card>
        )}

        {/* Workflow Information */}
        <Card size="small" title="Action Summary" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Analysis:</Text>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: "#fafafa",
                borderRadius: 4,
                fontSize: 13
              }}
            >
              {workflowInfo.analysis || <Text type="secondary">No analysis recorded</Text>}
            </div>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Text strong>Action Suggested:</Text>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: "#fff7e6",
                borderRadius: 4,
                fontSize: 13
              }}
            >
              {workflowInfo.actionSuggested || <Text type="secondary">No action suggested</Text>}
            </div>
          </div>
          
          <div>
            <Text strong>Action Taken:</Text>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: "#f6ffed",
                borderRadius: 4,
                fontSize: 13,
                border: workflowInfo.actionTaken ? "1px solid #b7eb8f" : "none"
              }}
            >
              {workflowInfo.actionTaken || <Text type="secondary">No action taken recorded</Text>}
            </div>
          </div>
          
          {workflowInfo.closedBy && (
            <div style={{ marginTop: 16, color: "#8c8c8c", fontSize: 12 }}>
              <Icon type="user" style={{ marginRight: 4 }} />
              Closed by <strong>{workflowInfo.closedBy.name}</strong>
              {workflowInfo.closedAt && (
                <span> on {moment(workflowInfo.closedAt).format("DD MMM YYYY, HH:mm:ss")}</span>
              )}
            </div>
          )}
        </Card>

        {/* Status History Timeline. The terminal ACTION_TAKEN entry of an
            Ignore closure is labelled Ignored so the timeline agrees with the
            Status column; its raw remark is still printed beside it. */}
        {statusHistory.length > 0 && (
          <Card size="small" title="Status History">
            <Timeline mode="left">
              {statusHistory.map((entry, index) => {
                const isIgnoreClosure =
                  entry.status === "ACTION_TAKEN" &&
                  index === lastActionTakenIndex &&
                  selectedAlert._status === "IGNORED";
                const statusInfo = getStatusInfo(
                  isIgnoreClosure ? "IGNORED" : entry.status
                );
                return (
                  <Timeline.Item
                    key={index}
                    color={statusInfo.color || "gray"}
                    label={
                      entry.date 
                        ? moment(entry.date).format("DD MMM YYYY, HH:mm")
                        : ""
                    }
                  >
                    <div>
                      <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                      {entry.remarks && (
                        <span style={{ marginLeft: 8, color: "#595959" }}>
                          - {entry.remarks}
                        </span>
                      )}
                    </div>
                    {entry.updatedBy && (
                      <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>
                        By: {entry.updatedBy.name}
                      </div>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </Card>
        )}
      </Drawer>
    );
  };

  render() {
    const { loading, filteredData, hasLoaded } = this.state;

    return (
      <Layout style={{ background: "transparent" }}>
        <Content>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16
            }}
          >
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Alerts Status Report
              </Title>
              <Text type="secondary">
                Review and export open and closed alerts with their workflow status and action details, for a selected created date range
              </Text>
            </div>
            <Button
              type="primary"
              icon="download"
              onClick={this.exportToCSV}
              disabled={filteredData.length === 0}
            >
              Export to CSV ({filteredData.length})
            </Button>
          </div>

          {/* Query scope - created date range */}
          {this.renderQueryBar()}

          {/* Stats, filters and table appear only after a range has been
              loaded, so empty tiles are never mistaken for real zeros. */}
          {hasLoaded && this.renderStatsCards()}

          {hasLoaded && this.renderFilters()}

          {hasLoaded ? (
            <Card bodyStyle={{ padding: 0 }}>
              <Table
                columns={this.getColumns()}
                dataSource={filteredData}
                rowKey={(record) => record._id || record.alertId}
                loading={loading}
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} alerts`
                }}
                scroll={{ x: 1950 }}
                size="small"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No alerts found for the selected created date range"
                    />
                  )
                }}
              />
            </Card>
          ) : (
            <Card>
              <Spin spinning={loading}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Select a created date range above and click 'Load Alerts' to run the report"
                />
              </Spin>
            </Card>
          )}

          {/* Details Drawer */}
          {this.renderDrawer()}
        </Content>
      </Layout>
    );
  }
}
